// Small colour helpers for medallions and swatches (hex strings only).

import type { Relation } from '../world/api.ts';

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX_SHORT = 4;
const FALLBACK: Rgb = { r: 120, g: 130, b: 140 };

export function parseHex(hex: string): Rgb {
  let h = hex.trim().replace('#', '');
  if (h.length === HEX_SHORT - 1) {
    h = [...h].map((c) => c + c).join('');
  }
  const n = Number.parseInt(h.slice(0, 6), 16);
  if (Number.isNaN(n)) {
    return FALLBACK;
  }
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function toHex({ r, g, b }: Rgb): string {
  const c = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Mix toward white (amount > 0) or black (amount < 0), amount in -1..1. */
export function shade(hex: string, amount: number): string {
  const c = parseHex(hex);
  const target = amount > 0 ? 255 : 0;
  const t = Math.abs(amount);
  return toHex({ r: c.r + (target - c.r) * t, g: c.g + (target - c.g) * t, b: c.b + (target - c.b) * t });
}

export function rgba(hex: string, alpha: number): string {
  const c = parseHex(hex);
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
}

/** Perceived luminance 0..1 (sRGB weights), to pick legible ink on a swatch. */
export function luminance(hex: string): number {
  const c = parseHex(hex);
  return (0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b) / 255;
}

/** Relation colours, mirrored from theme.css tokens (SVG attributes cannot read CSS vars). */
export const RELATION_COLOR: Record<Relation, string> = {
  self: '#e6c77a',
  war: '#d2412f',
  denounced: '#e58a2c',
  neutral: '#98a2aa',
  friend: '#5cbf62',
  ally: '#3f86d8',
};

export const RELATION_LABEL: Record<Relation, string> = {
  self: 'You',
  war: 'At war',
  denounced: 'Denounced',
  neutral: 'Neutral',
  friend: 'Friend',
  ally: 'Ally',
};

/** Ribbon and ledger order: the most pressing relation first. */
export const RELATION_RANK: Record<Relation, number> = {
  war: 0,
  denounced: 1,
  ally: 2,
  friend: 3,
  neutral: 4,
  self: 5,
};
